import React, {
  AriaAttributes,
  AriaRole,
  CSSProperties,
  DOMAttributes as ReactDOMAttributes,
} from 'react'
import invariant from 'tiny-invariant'
import { createContextWithHook } from '@chilipiper/utils'
import { Icon } from '../../../../old/icon'
import { Box, Flex, ScreenReaderOnly, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { PositioningProps } from '../../types'
import { ThemeColors } from '../../../theme'
import { EndContentProvider } from '../../data'

/** Any focusable element, including both HTML and SVG elements. */
interface FocusableElement extends Element, HTMLOrSVGElement {}

/** All DOM attributes supported across both HTML and SVG elements. */
interface DOMAttributes<T = FocusableElement> extends AriaAttributes, ReactDOMAttributes<T> {
  className?: string | undefined
  id?: string | undefined
  role?: AriaRole | undefined
  style?: CSSProperties | undefined
  tabIndex?: number | undefined
}

type Variant = 'major' | 'moderate'

type Props = {
  children: string | string[]
  description?: string
  icon?: React.ComponentType
  position?: LabelPosition
  variant?: Variant
  width?: 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 // min 64px max 560px
  endContent?: React.ReactNode
}

const variantProps: Record<Variant, { textColor: ThemeColors }> = {
  major: {
    textColor: 'text/form-label-major',
  },
  moderate: {
    textColor: 'text/form-label-moderate',
  },
}

const DEFAULT_POSITION: LabelPosition = 'left'

export const SettingLabel = (props: Props) => {
  if (props.position === 'hidden') {
    return (
      <ScreenReaderOnly>
        <SettingLabelInner {...props} />
      </ScreenReaderOnly>
    )
  }
  return <SettingLabelInner {...props} />
}

const SettingLabelInner = ({
  children,
  description,
  icon,
  endContent,
  width = 21,
  variant = 'major',
  position = DEFAULT_POSITION,
}: Props) => {
  const { labelProps, descriptionProps, labelElement, isRequired } = useSettingLabelContext()
  return (
    <Flex
      flexDirection='column'
      pb={position === 'left' ? 0 : 3}
      pt={position === 'left' ? 2 : 0}
      pr={7}
      w={position === 'left' ? width : undefined}
      flexShrink={0}
    >
      <Flex
        as={labelElement}
        display='flex'
        gap={3}
        alignItems='center'
        h={position === 'left' ? 6 : undefined}
        {...labelProps}
      >
        {icon && <Icon icon={icon} color='icon/indicator' w={4} h={4} />}
        <Text textStyle='form-label' color={variantProps[variant].textColor}>
          {children}
        </Text>
        {isRequired && (
          <Text textStyle='form-label-inline' color='text/action-caution'>
            *
          </Text>
        )}
        <EndContentProvider value={{ iconColor: 'icon/indicator', size: 4 }}>
          {endContent}
        </EndContentProvider>
      </Flex>
      {description && (
        <Text textStyle='body-multiline' color='text/body-moderate' mt={1} {...descriptionProps}>
          {description}
        </Text>
      )}
    </Flex>
  )
}

type LabelPosition = 'top' | 'left' | 'hidden'
type LabelElement = 'label' | 'div'

export const SettingLabelContainer = ({
  children,
  labelProps,
  descriptionProps,
  label,
  labelElement,
  isRequired,
  ...positioningProps
}: {
  children: React.ReactNode
  descriptionProps?: DOMAttributes
  label: React.ReactNode | string
  labelElement: LabelElement
  labelProps: DOMAttributes & React.LabelHTMLAttributes<HTMLLabelElement>
  isRequired: boolean | undefined
} & PositioningProps) => {
  validatePositioningProps(positioningProps, 'SettingLabelContainer')
  const { position } = parseLabel(label)
  return (
    <SettingLabelBaseProvider
      value={{ labelProps, descriptionProps: descriptionProps ?? {}, labelElement, isRequired }}
    >
      <Box display={position === 'top' ? 'block' : 'flex'} flexGrow={1} {...positioningProps}>
        {label}
        <Box flexGrow={1} overflow='hidden'>
          {children}
        </Box>
      </Box>
    </SettingLabelBaseProvider>
  )
}

const parseLabel = (label: React.ReactNode): { position: LabelPosition } => {
  invariant(
    label &&
      typeof label !== 'boolean' &&
      typeof label !== 'number' &&
      typeof label !== 'string' &&
      typeof label !== 'bigint' &&
      'type' in label &&
      typeof label.type !== 'string',
    'label must be a React element'
  )
  return {
    position: (label.props as Props).position ?? DEFAULT_POSITION,
  }
}

const { SettingLabelBaseProvider, useSettingLabelContext } = createContextWithHook<{
  descriptionProps: DOMAttributes
  isRequired: boolean | undefined
  labelElement: LabelElement
  labelProps: DOMAttributes
}>()('SettingLabel')
